import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Compass, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { NAV } from "../lib/nav";
import { tabsFor } from "../lib/bookTabs";
import { useCurrentBook } from "../lib/currentBook";

const LIBRARY = ["my-books", "series", "continuity"];
const TOOLS = ["manuscript", "rewrite", "proofread", "ebook", "paperback", "hardcover", "audiobook", "covers", "preflight", "versions", "exports"];
const ACCOUNT = ["trash", "support", "usage", "billing", "showcase", "readers", "settings"];
const cls = (on) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${on ? "bg-amber-500/10 text-amber-300" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"}`;

const Head = ({ children, onClick, open, testId }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={testId} className="w-full flex items-center justify-between px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500 hover:text-slate-300 disabled:hover:text-slate-500">
    {children}{onClick && <ChevronDown size={12} className={`transition-transform ${open ? "" : "-rotate-90"}`} />}
  </button>
);

export function SideNav({ onNavigate }) {
  const { user } = useAuth();
  const book = useCurrentBook();
  const loc = useLocation();
  const inBook = book && loc.pathname === `/app/books/${book.id}`;
  const novel = !inBook || !book.format || ["novel", "nonfiction"].includes(book.format);
  const [toolsOpen, setToolsOpen] = useState(() => localStorage.getItem("nav_tools") !== "0");
  const isAdmin = ["owner", "admin"].includes(user?.role);
  const items = (ids) => NAV.filter((n) => ids.includes(n.id) && (!n.admin || isAdmin) && (!n.owner || user?.role === "owner"));
  const link = (n) => (
    <NavLink key={n.id} to={n.to} end={n.to === "/app"} onClick={onNavigate} data-testid={`nav-${n.id}`} className={({ isActive }) => cls(isActive)}>
      <n.icon size={16} className="shrink-0" /><span className="truncate flex-1">{n.label}</span>
    </NavLink>
  );
  const tab = new URLSearchParams(loc.search).get("tab");
  const toggle = () => { localStorage.setItem("nav_tools", toolsOpen ? "0" : "1"); setToolsOpen(!toolsOpen); };
  return (
    <nav className="space-y-0.5" data-testid="side-nav">
      <button type="button" onClick={() => { window.dispatchEvent(new Event("open-search")); onNavigate?.(); }} className="w-full flex items-center gap-2 px-3 py-2 mb-1 rounded-lg border border-slate-800 text-sm text-slate-400 hover:border-amber-500/40 hover:text-slate-200" data-testid="nav-search">
        <Search size={14} /><span className="flex-1 text-left">Search & jump to…</span><kbd className="text-[10px] font-mono text-slate-600">Ctrl K</kbd>
      </button>
      {inBook && (<>
        <Head testId="nav-head-book">This {["comic", "manga"].includes(book.format) ? "comic" : book.format === "webtoon" ? "webtoon" : book.format === "poetry" ? "collection" : "book"}</Head>
        <p className="px-3 text-xs text-amber-200 truncate mb-1" title={book.title}>{book.title}</p>
        {tabsFor(book.format).map(([k, l]) => (
          <NavLink key={k} to={`/app/books/${book.id}?tab=${k}`} onClick={onNavigate} data-testid={`nav-book-${k}`} className={() => `${cls(tab === k)} py-1.5 text-[13px] pl-6`}>{l}</NavLink>
        ))}
      </>)}
      <Head testId="nav-head-library">Library</Head>
      {items(LIBRARY).filter((n) => novel || n.id !== "continuity").map(link)}
      {novel && (<>
        <Head onClick={toggle} open={toolsOpen} testId="nav-head-tools">Book tools</Head>
        {toolsOpen && items(TOOLS).map(link)}
      </>)}
      <Head testId="nav-head-account">Account & help</Head>
      {items(ACCOUNT).map(link)}
      <button type="button" onClick={() => { window.dispatchEvent(new Event("open-tour")); onNavigate?.(); }} className={cls(false) + " w-full"} data-testid="nav-tour"><Compass size={16} className="shrink-0" />Take the tour</button>
    </nav>
  );
}
